import { ConductorAllChildrenActive, Router, ScreenBase } from "@frui.ts/screens";
import LocalizationService from "../../services/localizationService";
import AppRouter from "services/appRouter";
import ContactViewModel from "../common/contactViewModel";
import { action, computed, observable, reaction } from "mobx";
import * as types from "viewModels/common/types";
import SummaryViewModel from "./summaryMultiEshopViewModel";
import PaymentService from "../../services/payment/paymentService";
import React from "react";
import OutageService from "../../services/outageService";
import { getStatusSteps } from "./multiEshopStatusSteps";
import BatchOrderViewModel from "./batchOrderViewModel";
import EnumService from "services/enumService";
import ChargeOrderService from "services/chargeOrderService";
import { scrollToElement, scrollToFirstError } from "utils/scrollTo";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { validate } from "@frui.ts/validation";
import { interfaces } from "inversify";
import ChargeOrderContactItem from "models/chargeOrderContactItem";
import {
  EmailConfirmationValidationRules,
  EmailNotificationValidationRules,
  EmailValidationRules,
  NotificationValidationRules,
} from "models/notificationPreferences";
import { isMobile } from "react-device-detect";

export const ContactMultiEshopViewModelNavigationName = "contact";
@Router.registerRoute({ name: "contact", route: ContactMultiEshopViewModelNavigationName })
export default class ContactMultiEshopViewModel extends ConductorAllChildrenActive<ScreenBase> {
  private unsubscription: Array<() => void> = [];
  turnstileRef: any;

  tg = (code: string) => this.localizationService.translateGeneral(`multiEshop.${code}`);

  @observable formElement?: HTMLFormElement;
  @observable contactViewModel: ContactViewModel;
  @observable summaryViewModel: SummaryViewModel;
  @observable errorMessage?: types.ErrorMessage;

  statusNavigation = getStatusSteps();

  constructor(
    public chargeOrderContactItem: ChargeOrderContactItem,
    public localizationService: LocalizationService,
    public outageService: OutageService,
    public enumService: EnumService,
    private contactViewModelFactory: ReturnType<typeof ContactViewModel.Factory>,
    private summaryViewModelFactory: ReturnType<typeof SummaryViewModel.Factory>,
    private chargeOrderService: ChargeOrderService,
    private paymentService: PaymentService,
    private router: AppRouter
  ) {
    super();
    this.navigationName = ContactMultiEshopViewModelNavigationName;
    this.children.clear();
    this.contactViewModel = this.contactViewModelFactory(this.chargeOrderContactItem, false, true);
    this.summaryViewModel = this.summaryViewModelFactory(false, false, this.onContinue, this.onBack);
    this.children.push(this.contactViewModel, this.summaryViewModel);
    attachAutomaticBlurValidator(this.notificationPreferences, {
      ...EmailValidationRules,
      ...EmailConfirmationValidationRules,
      ...EmailNotificationValidationRules,
      ...NotificationValidationRules,
    });
  }

  protected onInitialize() {
    this.paymentService.resetRedirect();
    this.unsubscription.push(
      reaction(() => this.notificationPreferences._isNotificationEmailSame, this.onSameNotificationEmailChanged),
      reaction(() => this.notificationPreferences._emailAddressNotification, this.onEmailAddressNotification),
      reaction(() => this.notificationPreferences.emailAddress, this.onEmailAdressChanged),
      reaction(() => this.notificationPreferences.phoneNumber, this.onPhoneNumberChanged)
    );

    super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscription.forEach(unsubscribe => unsubscribe());
      this.unsubscription = [];
    }
    return super.onDeactivate(isClosing);
  }
  protected async onActivate(): Promise<any> {
    if (isMobile && this.stepper) {
      scrollToElement(this.stepper);
    }
    this.chargeOrderService.chargeOrder._origin = "multiple";
    this.checkStoredNotificationPreferences();
    if (this.notificationPreferences._notificationEnabled) {
    }
    super.onActivate();
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @computed
  get transferDisabled() {
    return this.chargeOrderService.isTransferDisabled;
  }

  @computed
  get notificationPreferences() {
    return this.chargeOrderContactItem.notificationPreferences;
  }

  @computed
  get onlySingleChargeType() {
    const singleCharges = this.chargeOrderService.chargeOrderItems.filter(item => item._singleChargeType);
    return this.chargeOrderService.chargeOrderItems.length === singleCharges.length;
  }

  private checkStoredNotificationPreferences() {
    const chargeOrder = this.chargeOrderService.chargeOrder;
    if (chargeOrder.emailAddress && !this.onlySingleChargeType) {
      this.chargeOrderContactItem.notificationPreferences.emailAddress = chargeOrder.emailAddress;
      this.chargeOrderContactItem.notificationPreferences._notificationEnabled =
        chargeOrder.notificationPreferences._notificationEnabled;
      this.chargeOrderContactItem.notificationPreferences._emailConfirmation =
        chargeOrder.notificationPreferences._emailConfirmation;
      this.chargeOrderContactItem.notificationPreferences.emailNotificationEnabled =
        chargeOrder.notificationPreferences.emailNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences._isNotificationEmailSame =
        chargeOrder.notificationPreferences._isNotificationEmailSame;
      this.chargeOrderContactItem.notificationPreferences.smsNotificationEnabled =
        chargeOrder.notificationPreferences.smsNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences.phoneNumber = chargeOrder.notificationPreferences.phoneNumber;
      return;
    }
    if (chargeOrder.emailAddress && this.onlySingleChargeType) {
      this.chargeOrderContactItem.notificationPreferences = chargeOrder.notificationPreferences =
        this.chargeOrderService.resetNotificationPreferences(this.chargeOrderContactItem.notificationPreferences);
      this.chargeOrderContactItem.notificationPreferences.emailAddress = chargeOrder.emailAddress;
      this.chargeOrderContactItem.notificationPreferences._emailConfirmation = chargeOrder.emailAddress;

      this.chargeOrderContactItem.notificationPreferences._notificationEnabled =
        chargeOrder.notificationPreferences._notificationEnabled;

      this.chargeOrderContactItem.notificationPreferences.emailNotificationEnabled =
        chargeOrder.notificationPreferences.emailNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences._isNotificationEmailSame =
        chargeOrder.notificationPreferences._isNotificationEmailSame;
      this.chargeOrderContactItem.notificationPreferences.smsNotificationEnabled =
        chargeOrder.notificationPreferences.smsNotificationEnabled;
      this.chargeOrderContactItem.notificationPreferences.phoneNumber = chargeOrder.notificationPreferences.phoneNumber;
      this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
      return;
    } else {
      this.chargeOrderContactItem.notificationPreferences = chargeOrder.notificationPreferences =
        this.chargeOrderService.resetNotificationPreferences(this.chargeOrderContactItem.notificationPreferences);
      this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
    }
  }

  @action.bound
  public onContinue() {
    if (!this.validateAll()) {
      return;
    }
    this.updateChargeOrderItem();
    this.router.navigate("payment");
  }

  @action.bound
  public onBack() {
    this.updateChargeOrderItem();
    this.router.navigate("recap");
  }

  @action.bound
  private validateAll() {
    const isValid = validate(this.chargeOrderContactItem.notificationPreferences);
    if (!isValid) {
      scrollToFirstError(this.formElement);
    }

    return isValid;
  }

  @action.bound
  resetChargeOrder() {
    this.chargeOrderService.reset();
    this.router.navigate(BatchOrderViewModel);
  }

  @action.bound
  onSameNotificationEmailChanged() {
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
      this.notificationPreferences.emailNotificationEnabled = true;
      return;
    }
    this.notificationPreferences._emailAddressNotification = undefined;
  }

  @action.bound
  onEmailAdressChanged() {
    if (!this.notificationPreferences.emailAddress) {
      this.notificationPreferences._isNotificationEmailSame = false;
    }
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
      return;
    }
  }

  @action.bound
  onEmailAddressNotification() {
    const isEmailSet =
      this.notificationPreferences._emailAddressNotification !== undefined &&
      this.notificationPreferences._emailAddressNotification.length > 0;
    this.notificationPreferences.emailNotificationEnabled = isEmailSet;
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
      return;
    }
  }

  @action.bound
  onPhoneNumberChanged() {
    const isPhoneNumberSet =
      this.notificationPreferences.phoneNumber !== undefined && this.notificationPreferences.phoneNumber.length > 0;
    this.notificationPreferences.smsNotificationEnabled = isPhoneNumberSet;
    if (this.notificationPreferences.phoneNumber) {
      this.contactViewModel.phoneNumberChanged = true;
      return;
    }
  }

  @action.bound
  private updateChargeOrderItem() {
    const chargeOrder = this.chargeOrderService.chargeOrder;
    if (this.notificationPreferences.emailAddress) {
      chargeOrder.emailAddress = this.notificationPreferences.emailAddress;
    }
    if (this.notificationPreferences && !this.onlySingleChargeType) {
      chargeOrder.notificationPreferences = this.notificationPreferences;
    }
    this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
  }

  @action.bound
  translate(code: string) {
    return this.localizationService.translateGeneral(`multiEshop.${code}`);
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeOrderContactItem: ChargeOrderContactItem) => {
      return new ContactMultiEshopViewModel(
        chargeOrderContactItem,
        container.get(LocalizationService),
        container.get(OutageService),
        container.get(EnumService),
        container.get(ContactViewModel.Factory),
        container.get(SummaryViewModel.Factory),
        container.get(ChargeOrderService),
        container.get(PaymentService),
        container.get(AppRouter)
      );
    };
  }
}
